import * as Dialog from '@radix-ui/react-dialog';
import { X, TriangleAlert, CheckCircle2, Info, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { cn } from './cn';
import { Button } from './Button';
import { isInsidePopoverPortal } from './popoverPortal';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Sticky footer (e.g. Cancel / Save buttons). */
  footer?: ReactNode;
  /** Max width in px (caps to viewport). */
  width?: number;
  className?: string;
  /** antd `maskClosable={false}` — a backdrop click does NOT close the dialog (guards forms). */
  maskClosable?: boolean;
  /** antd `keyboard={false}` — Esc does NOT close the dialog. */
  keyboard?: boolean;
  /**
   * Pass `false` when the dialog has nothing to type into.
   *
   * Radix moves focus to the first focusable child on open, which is right for a form (the cursor
   * lands in the first field) and wrong for a record: there the first focusable thing is whatever
   * chip happens to come first — a click-to-copy identifier in the title, say — and focusing it pops
   * its tooltip over the header the moment the dialog appears. Focus then rests on the dialog
   * itself, so the trap, Esc and the screen-reader announcement are unchanged.
   */
  autoFocus?: boolean;
}

/**
 * Centered dialog (replaces antd Modal) built on Radix Dialog — focus-trapped,
 * Esc/overlay close, accessible. Header + scrollable body + sticky footer; the
 * body scrolls while header/footer stay pinned.
 */
function ModalBase({ open, onOpenChange, title, description, children, footer, width = 560, className, maskClosable, keyboard, autoFocus }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[201] flex max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'w-[calc(100vw-2rem)] rounded-card border border-line bg-surface shadow-pop',
            'data-[state=open]:animate-pop-in',
            className,
          )}
          style={{ width: `min(${width}px, calc(100vw - 2rem))` }}
          // antd parity: maskClosable={false} → backdrop click is a no-op; keyboard={false} → Esc is a no-op.
          // A calendar opened from inside this dialog lives in the shared popup portal (see
          // popoverPortal.ts), i.e. OUTSIDE this content node — so picking a date reads to Radix as
          // an outside click. Never let that dismiss the dialog and discard the user's form.
          onPointerDownOutside={(e) => { if (maskClosable === false || isInsidePopoverPortal(e)) e.preventDefault(); }}
          onInteractOutside={(e) => { if (maskClosable === false || isInsidePopoverPortal(e)) e.preventDefault(); }}
          onEscapeKeyDown={keyboard === false ? (e) => e.preventDefault() : undefined}
          // `autoFocus={false}` → keep focus on the dialog rather than its first focusable child.
          onOpenAutoFocus={autoFocus === false
            ? (e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus(); }
            : undefined}
        >
          {/* Always render a Title for a11y; hide it visually when none is provided. */}
          {/* Header + footer are tinted shelves, so a dialog reads as three bands (title / work /
              actions) instead of one white sheet with two rules drawn on it. The radii are the
              dialog's own minus its 1px border, so the tints follow the rounded corners. */}
          <div className={cn(title != null || description != null ? 'flex items-start justify-between gap-4 rounded-t-[calc(var(--radius-card)_-_1px)] border-b border-line bg-linear-to-b from-brand-500/[0.08] to-transparent px-5 py-4' : 'contents')}>
            <div className="min-w-0">
              <Dialog.Title className={cn(title != null ? 'type-section-title text-content' : 'sr-only')}>
                {title ?? 'Dialog'}
              </Dialog.Title>
              {description != null && <Dialog.Description className="mt-0.5 type-body text-muted">{description}</Dialog.Description>}
            </div>
            <Dialog.Close
              className={cn(
                'grid size-8 shrink-0 place-items-center rounded-control text-muted transition-colors hover:bg-surface-2 hover:text-content',
                title == null && description == null && 'absolute right-3 top-3 z-10',
              )}
              aria-label="Close"
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {/* Wraps: a three-button footer (Cancel · Preview · Download) does not fit a 390px phone on
              one line, and without this the leftmost button is simply cut off the edge. */}
          {footer != null && (
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-[calc(var(--radius-card)_-_1px)] border-t border-line bg-surface-2/70 px-5 py-3">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Imperative Modal.confirm / .info / .success / .error / .warning ───────────
interface ConfirmConfig {
  title?: ReactNode;
  content?: ReactNode;
  icon?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: 'primary' | 'danger';
  okButtonProps?: { danger?: boolean; loading?: boolean };
  onOk?: () => void | Promise<unknown>;
  onCancel?: () => void;
  width?: number;
  /** false → single OK button (used by info/success/error/warning). */
  okCancel?: boolean;
  type?: 'confirm' | 'info' | 'success' | 'error' | 'warning';
}

const TYPE_ICON: Record<string, ReactNode> = {
  confirm: <TriangleAlert className="text-warning-500" />,
  warning: <TriangleAlert className="text-warning-500" />,
  info: <Info className="text-sky-500" />,
  success: <CheckCircle2 className="text-brand-600" />,
  error: <XCircle className="text-danger-500" />,
};

function spawnConfirm(config: ConfirmConfig) {
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  const destroy = () => { setTimeout(() => { root.unmount(); host.remove(); }, 200); };

  function ConfirmDialog() {
    const [open, setOpen] = useState(true);
    const [busy, setBusy] = useState(false);
    const close = () => setOpen(false);
    const cancel = () => { close(); config.onCancel?.(); destroy(); };
    const ok = async () => {
      try { setBusy(true); await config.onOk?.(); close(); destroy(); }
      catch { setBusy(false); /* keep open on rejection, antd parity */ }
    };
    return (
      <ModalBase
        open={open}
        onOpenChange={(o) => { if (!o) cancel(); }}
        width={config.width ?? 440}
        footer={
          <>
            {config.okCancel !== false && <Button variant="default" onClick={cancel}>{config.cancelText ?? 'Cancel'}</Button>}
            <Button variant={config.okType === 'danger' || config.okButtonProps?.danger ? 'danger' : 'primary'} loading={busy || config.okButtonProps?.loading} onClick={ok}>
              {config.okText ?? 'OK'}
            </Button>
          </>
        }
      >
        <div className="flex gap-3.5">
          <span className="mt-0.5 shrink-0 [&>svg]:size-6">{config.icon ?? TYPE_ICON[config.type ?? 'confirm']}</span>
          <div className="min-w-0">
            {config.title != null && <div className="type-section-title text-content">{config.title}</div>}
            {config.content != null && <div className="mt-1 type-body text-muted">{config.content}</div>}
          </div>
        </div>
      </ModalBase>
    );
  }
  root.render(<ConfirmDialog />);
  return { destroy };
}

export const Modal = Object.assign(ModalBase, {
  confirm: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'confirm' }),
  info: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'info', okCancel: false }),
  success: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'success', okCancel: false }),
  error: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'error', okCancel: false }),
  warning: (c: ConfirmConfig) => spawnConfirm({ ...c, type: 'warning', okCancel: false }),
});
